import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  estimateCostUsd,
  formatUsd,
  KNOWN_PROVIDERS,
} from '@/lib/aiPricing';

// ============================================================
// Superadmin — Usage & coût des tokens IA de toute la plateforme.
// Agrège `zifek_api_logs` : total de tokens, coût estimé, coût par
// utilisateur (pour savoir combien chaque utilisateur coûte en IA),
// coût par modèle, et timeline. Un sélecteur permet de basculer
// d'une API (provider) à une autre.
// ============================================================

interface ApiLog {
  id: number;
  provider: string | null;
  model: string | null;
  request_tokens: number | null;
  response_tokens: number | null;
  duration_ms: number | null;
  status: string | null;
  user_id: number | null;
  created_at: string;
}

interface UserLite {
  id: number;
  name: string | null;
  user_name: string | null;
  email: string | null;
  nomcommerce: string | null;
}

type Period = '7' | '30' | '90' | 'all';

interface UserStat {
  userId: number | null;
  label: string;
  email: string;
  calls: number;
  tokens: number;
  cost: number;
}

interface ModelStat {
  model: string;
  calls: number;
  tokens: number;
  cost: number;
}

export default function AiUsagePage() {
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [users, setUsers] = useState<Record<number, UserLite>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [period, setPeriod] = useState<Period>('30');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('zifek_api_logs')
        .select('id, provider, model, request_tokens, response_tokens, duration_ms, status, user_id, created_at')
        .order('created_at', { ascending: false })
        .limit(20000);

      if (period !== 'all') {
        const since = new Date();
        since.setDate(since.getDate() - parseInt(period, 10));
        query = query.gte('created_at', since.toISOString());
      }

      const { data, error: logErr } = await query;
      if (logErr) throw logErr;
      const rows = (data as ApiLog[]) || [];
      setLogs(rows);

      const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter((v): v is number => v != null)));
      if (userIds.length > 0) {
        const { data: userRows } = await supabase
          .from('users')
          .select('id, name, user_name, email, nomcommerce')
          .in('id', userIds);
        const map: Record<number, UserLite> = {};
        (userRows as UserLite[] || []).forEach((u) => { map[u.id] = u; });
        setUsers(map);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Liste des providers réellement présents dans les logs.
  const availableProviders = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => { if (l.provider) set.add(l.provider); });
    return Array.from(set);
  }, [logs]);

  const filtered = useMemo(
    () => (providerFilter === 'all' ? logs : logs.filter((l) => l.provider === providerFilter)),
    [logs, providerFilter],
  );

  const totals = useMemo(() => {
    let tokens = 0;
    let cost = 0;
    let success = 0;
    let duration = 0;
    filtered.forEach((l) => {
      const t = (l.request_tokens || 0) + (l.response_tokens || 0);
      tokens += t;
      cost += estimateCostUsd(l.provider || '', l.model || '', l.request_tokens || 0, l.response_tokens || 0);
      if (l.status === 'success') success += 1;
      duration += l.duration_ms || 0;
    });
    return {
      calls: filtered.length,
      tokens,
      cost,
      successRate: filtered.length > 0 ? Math.round((success / filtered.length) * 100) : 0,
      avgDuration: filtered.length > 0 ? Math.round(duration / filtered.length) : 0,
    };
  }, [filtered]);

  const userStats = useMemo<UserStat[]>(() => {
    const map = new Map<string, UserStat>();
    filtered.forEach((l) => {
      const key = l.user_id != null ? String(l.user_id) : 'system';
      const u = l.user_id != null ? users[l.user_id] : null;
      const existing = map.get(key) || {
        userId: l.user_id,
        label: u ? (u.nomcommerce || u.name || u.user_name || `Utilisateur #${l.user_id}`) : 'Système / non attribué',
        email: u?.email || '',
        calls: 0,
        tokens: 0,
        cost: 0,
      };
      existing.calls += 1;
      existing.tokens += (l.request_tokens || 0) + (l.response_tokens || 0);
      existing.cost += estimateCostUsd(l.provider || '', l.model || '', l.request_tokens || 0, l.response_tokens || 0);
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
  }, [filtered, users]);

  const modelStats = useMemo<ModelStat[]>(() => {
    const map = new Map<string, ModelStat>();
    filtered.forEach((l) => {
      const key = l.model || 'inconnu';
      const existing = map.get(key) || { model: key, calls: 0, tokens: 0, cost: 0 };
      existing.calls += 1;
      existing.tokens += (l.request_tokens || 0) + (l.response_tokens || 0);
      existing.cost += estimateCostUsd(l.provider || '', l.model || '', l.request_tokens || 0, l.response_tokens || 0);
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost).slice(0, 12);
  }, [filtered]);

  const providerStats = useMemo(() => {
    const map = new Map<string, { provider: string; calls: number; tokens: number; cost: number }>();
    filtered.forEach((l) => {
      const key = l.provider || 'inconnu';
      const existing = map.get(key) || { provider: key, calls: 0, tokens: 0, cost: 0 };
      existing.calls += 1;
      existing.tokens += (l.request_tokens || 0) + (l.response_tokens || 0);
      existing.cost += estimateCostUsd(l.provider || '', l.model || '', l.request_tokens || 0, l.response_tokens || 0);
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
  }, [filtered]);

  const maxUserCost = userStats.length > 0 ? userStats[0].cost || 1 : 1;
  const fmtTokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
  const fmtDuration = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-coin-line mr-2 text-primary-500"></i>Usage &amp; coût des tokens IA
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Consommation IA de toute la plateforme : tokens, coût estimé et coût par utilisateur
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={load} className="ml-auto text-xs font-medium underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setProviderFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${providerFilter === 'all' ? 'bg-foreground-950 text-background-50' : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
          >
            Toutes les API
          </button>
          {availableProviders.map((p) => {
            const meta = KNOWN_PROVIDERS.find((k) => k.id === p);
            return (
              <button
                key={p}
                onClick={() => setProviderFilter(p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${providerFilter === p ? 'bg-foreground-950 text-background-50' : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'}`}
              >
                <i className={meta?.icon || 'ri-server-line'}></i>
                {meta?.label || p}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 self-start">
          {(['7', '30', '90', 'all'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              {p === 'all' ? 'Tout' : `${p} j`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[
              { icon: 'ri-phone-line', label: 'Appels IA', value: totals.calls.toLocaleString('fr-FR'), color: 'bg-primary-100 text-primary-700' },
              { icon: 'ri-text-spacing', label: 'Tokens', value: fmtTokens(totals.tokens), color: 'bg-accent-100 text-accent-700' },
              { icon: 'ri-money-dollar-circle-line', label: 'Coût total', value: formatUsd(totals.cost), color: 'bg-secondary-100 text-secondary-700' },
              { icon: 'ri-check-double-line', label: 'Taux succès', value: `${totals.successRate}%`, color: 'bg-background-200/60 text-foreground-700' },
              { icon: 'ri-time-line', label: 'Durée moy.', value: fmtDuration(totals.avgDuration), color: 'bg-background-200/60 text-foreground-700' },
            ].map((s) => (
              <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
                  <i className={`${s.icon} text-sm`}></i>
                </div>
                <div className="text-xl font-bold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs text-foreground-500">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Coût par utilisateur */}
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-user-line text-primary-500"></i>Coût par utilisateur
              </h3>
              <p className="text-xs text-foreground-400 mb-4">Combien chaque utilisateur consomme en IA</p>
              {userStats.length === 0 ? (
                <p className="text-sm text-foreground-400 py-6 text-center">Aucune donnée</p>
              ) : (
                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                  {userStats.map((u, i) => (
                    <div key={`${u.userId ?? 'sys'}-${i}`} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-foreground-900 truncate">{u.label}</span>
                          <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatUsd(u.cost)}</span>
                        </div>
                        {u.email && <p className="text-[11px] text-foreground-400 truncate">{u.email}</p>}
                        <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-primary-500 rounded-full" style={{ width: `${Math.max(4, (u.cost / maxUserCost) * 100)}%` }} />
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-foreground-400">
                          <span>{u.calls} appel(s)</span>
                          <span>{fmtTokens(u.tokens)} tokens</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Coût par provider */}
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-server-line text-accent-500"></i>Coût par API
              </h3>
              <p className="text-xs text-foreground-400 mb-4">Répartition par fournisseur d&apos;IA</p>
              {providerStats.length === 0 ? (
                <p className="text-sm text-foreground-400 py-6 text-center">Aucune donnée</p>
              ) : (
                <div className="space-y-3">
                  {providerStats.map((p) => {
                    const meta = KNOWN_PROVIDERS.find((k) => k.id === p.provider);
                    const pct = totals.cost > 0 ? Math.round((p.cost / totals.cost) * 100) : 0;
                    return (
                      <div key={p.provider} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${meta?.icon || 'ri-server-line'} text-sm text-foreground-600`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-foreground-900">{meta?.label || p.provider}</span>
                            <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatUsd(p.cost)}</span>
                          </div>
                          <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-accent-500 rounded-full" style={{ width: `${Math.max(4, pct)}%` }} />
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-foreground-400">
                            <span>{p.calls} appel(s)</span>
                            <span>{fmtTokens(p.tokens)} tokens</span>
                            <span>{pct}% du coût</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Coût par modèle */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-6">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
              <i className="ri-cpu-line text-secondary-700"></i>Coût par modèle
            </h3>
            {modelStats.length === 0 ? (
              <p className="text-sm text-foreground-400 py-4 text-center">Aucune donnée</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-foreground-500">Modèle</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-foreground-500">Appels</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-foreground-500">Tokens</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-foreground-500">Coût estimé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelStats.map((m) => (
                      <tr key={m.model} className="border-b border-background-200/70 last:border-0">
                        <td className="px-3 py-2.5 text-xs font-mono text-foreground-700">{m.model}</td>
                        <td className="px-3 py-2.5 text-right text-xs text-foreground-600">{m.calls}</td>
                        <td className="px-3 py-2.5 text-right text-xs text-foreground-600">{fmtTokens(m.tokens)}</td>
                        <td className="px-3 py-2.5 text-right text-xs font-semibold text-foreground-900">{formatUsd(m.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            Le coût est une estimation basée sur les tarifs publics des modèles. Il permet de comparer les utilisateurs et les API entre eux.
          </p>
        </>
      )}
    </div>
  );
}